import { UUID } from "../functions/UUID"
import type { GUID } from "../types/GUID"

/**
 * Basic broadcast mechanism that forwards messages to active consumers.
 * Does not maintain memory of past messages.
 */

export type BroadcastInterface<T> = {
  push: (item: T) => void
  stop: (error: Error | undefined) => void
  subscribe: () => AsyncGenerator<T>
}

export function Broadcast<T>(initial?: T[]): BroadcastInterface<T> {
  // Closure state
  let is_completed = false
  let error: Error | undefined = undefined
  const callbacks = new Map<GUID, (v: true) => void>()
  const consumers = new Map<GUID, T[]>()
  const messages: T[] = initial || []

  // Keep creating promise pairs and waiting until completion
  const drain = (uuid: GUID) => {
    const values = consumers.get(uuid)!
    return values.splice(0, values.length)
  }

  const internal_subscribe = async function* (uuid: GUID): AsyncGenerator<T> {
    while (!is_completed) {
      const promise = new Promise<true>(resolve => callbacks.set(uuid, resolve))
      yield* drain(uuid)
      await promise
    }

    yield* drain(uuid)

    if (error) {
      throw error
    }
  }

  return {
    /**
     * Emits a new item to all active subscribers
     * @param item The item to broadcast to all subscribers
     */
    push(item: T): void {
      // Copy the array and assign a blank one
      consumers.forEach(array => array.push(item))
      callbacks.forEach(resolve => resolve(true))
    },

    /**
     * Emits a completion signal to all subscribers, optionally with an error
     * @param error Optional error to provide to subscribers
     */
    stop(e: Error | undefined): void {
      if (e) error = e
      is_completed = true
      callbacks.forEach(resolve => resolve(true))
    },

    /**
     * Subscribe to the broadcast stream
     * @returns An AsyncGenerator that yields items as they are emitted
     */
    subscribe(): AsyncGenerator<T> {
      const uuid = UUID()
      consumers.set(uuid, [...messages])
      return internal_subscribe(uuid)
    },
  }
}
